/**
 * @fileoverview Shared Login schema used by both frontend and backend.
 * Immutable login records storing authentication attempts.
 */

import { z } from 'zod';
import { commonValidators, createCrudSchemas, createEntityIdSchema, createdAtSchema, getSchemaFields } from './base.js';
import { ID_PATTERNS } from './types.js';

/**
 * Login validation schema (immutable records).
 * Login rows are immutable snapshots of authentication attempts.
 */
export const LoginSchema = z.object({
  id: createEntityIdSchema(ID_PATTERNS.LOGIN, 'ID must be in lo-prefixed nanoid7 format'),
  account_id: commonValidators.foreignKey('Account ID'),
  ip_address: z.string()
    .min(1, 'IP address is required')
    .max(45, 'IP address must be 45 characters or less'),
  user_agent: commonValidators.optionalString(1000),
  device: commonValidators.optionalString(255),
  success: z.boolean(),
  failure_reason: commonValidators.optionalString(80),
  metadata: commonValidators.jsonField('Metadata'),
  created_at: createdAtSchema,
});

const { CreateSchema, UpdateSchema } = createCrudSchemas(LoginSchema, { omitDeletedAt: false });

export const LoginCreateSchema = CreateSchema;
export const LoginUpdateSchema = UpdateSchema;

export type Login = z.infer<typeof LoginSchema>;
export type LoginCreate = z.infer<typeof LoginCreateSchema>;
export type LoginUpdate = z.infer<typeof LoginUpdateSchema>;

export const LOGIN_FIELDS = getSchemaFields(LoginSchema);
